import { useMemo, useRef } from 'react';
import type { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import { debounce } from 'lodash';

import { useFilterState, type FilterDescriptor } from '../../DataView/useFilterState';
import type { DataTableColumnMeta, ExtraFilter, FilterMode, ServerFilterVariables } from '../types';

type UseDataTableFiltersOptions<TData, TFilter extends Record<string, unknown>> = {
    columns: ColumnDef<TData, unknown>[];
    filterMode: FilterMode;
    enableUrlSync?: boolean;
    defaultFilters?: Partial<TFilter>;
    // Scope imposé, tenu HORS de `filterState` : il ne doit ni allumer « Effacer », ni disparaître à la
    // réinitialisation, ni dépendre de l'URL pour survivre à un rechargement.
    pinnedFilters?: Partial<TFilter>;
    searchFilterKey?: keyof TFilter;
    refetch?: (variables: ServerFilterVariables<TFilter>) => Promise<unknown>;
    // Standalone (non-column) filters that still participate in URL sync.
    extraFilters?: ExtraFilter[];
};

/**
 * Adaptateur TanStack au-dessus de `useFilterState` : il dérive les descripteurs de filtre des colonnes
 * et des filtres autonomes, puis n'ajoute que ce qui est propre à la table — la traduction en
 * `ColumnFiltersState`, le filtre global, et le refetch débouncé du mode serveur.
 */
export function useDataTableFilters<TData, TFilter extends Record<string, unknown>>({
    columns,
    filterMode,
    enableUrlSync = false,
    defaultFilters,
    pinnedFilters,
    searchFilterKey,
    refetch,
    extraFilters,
}: UseDataTableFiltersOptions<TData, TFilter>) {
    // La correspondance colonne ↔ clé de filtre ne sert qu'ici : `filterState` est indexé par clé de
    // filtre, TanStack par identifiant de colonne.
    const filterConfigs = useMemo(() => {
        const configs: { columnId: string; filterKey: string; type: string; defaultValue?: unknown }[] = [];
        for (const col of columns) {
            const meta = col.meta as DataTableColumnMeta<TFilter> | undefined;
            if (meta?.filter) {
                const columnId = 'accessorKey' in col ? String(col.accessorKey) : 'id' in col ? String(col.id) : '';
                const filterKey = meta.filter.filterKey ? String(meta.filter.filterKey) : columnId;
                configs.push({
                    columnId,
                    filterKey,
                    type: meta.filter.type,
                    defaultValue: meta.filter.defaultValue,
                });
            }
        }
        return configs;
    }, [columns]);

    const descriptors = useMemo<FilterDescriptor[]>(
        () => [
            ...filterConfigs.map(({ filterKey, type, defaultValue }) => ({
                key: filterKey,
                type: type as FilterDescriptor['type'],
                defaultValue,
            })),
            ...(extraFilters ?? []).map(({ key, type }) => ({ key, type })),
        ],
        [filterConfigs, extraFilters],
    );

    const state = useFilterState<TFilter>({
        filters: descriptors,
        enableUrlSync,
        searchKey: String(searchFilterKey || 'searchValue'),
        defaultFilters,
        pinnedFilters,
    });

    const { filterState, serverFilters, searchValue } = state;

    // Debounced refetch for server mode
    // Use a ref so the debounced function is stable even if the refetch prop changes identity
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    const debouncedRefetch = useMemo(() => {
        if (filterMode !== 'server') return null;
        return debounce((sortBy: string | undefined, sortOrder: string | undefined, filters: Partial<TFilter>) => {
            refetchRef.current?.({ sortBy, sortOrder, filters });
        }, 300);
    }, [filterMode]);

    // Convert to TanStack ColumnFiltersState (for client mode)
    const columnFiltersState: ColumnFiltersState = useMemo(() => {
        if (filterMode !== 'client') return [];
        const filters: ColumnFiltersState = [];

        for (const config of filterConfigs) {
            const value = filterState[config.filterKey as keyof TFilter];
            if (value === undefined || value === null || value === '') continue;
            filters.push({ id: config.columnId, value });
        }

        return filters;
    }, [filterMode, filterConfigs, filterState]);

    // Global filter for client-side search
    const globalFilter = filterMode === 'client' ? searchValue : undefined;

    return {
        ...state,
        serverFilters,
        columnFiltersState,
        globalFilter,
        debouncedRefetch,
        filterConfigs,
    };
}
